import { Skeleton, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { formatElapsed } from "../../helpers/elapsed.ts"
import type { Loadable } from "../../hooks/use-tracking-overview.ts"
import type { Measure, TrackingSummary } from "../../types/tracking.types.ts"

const { Text } = Typography

function Figure({
  label,
  value,
  aside
}: {
  label: string
  value: string
  aside?: string
}) {
  return (
    <div className={"min-w-[150px] flex-1"}>
      <Text type={"secondary"} className={"block text-xs"}>
        {label}
      </Text>
      <Text strong className={"text-base"}>
        {value}
      </Text>
      {aside && (
        <Text type={"secondary"} className={"block text-xs"}>
          {aside}
        </Text>
      )}
    </div>
  )
}

const percent = (rate: number | null) =>
  rate == null ? "—" : `${Math.round(rate * 100)}%`

export function TrackingSummaryStrip({
  summary
}: {
  summary: Loadable<TrackingSummary>
}) {
  const { t } = useTranslation()
  if (summary.status === "error") {
    return (
      <Text type={"secondary"}>{t("esign.tracking.strip.unavailable")}</Text>
    )
  }
  const data = summary.data
  if (!data) return <Skeleton active paragraph={{ rows: 1 }} title={false} />

  const median = (measure: Measure) => formatElapsed(measure.median_seconds, t)
  return (
    <div
      data-testid={"tracking-summary-strip"}
      className={
        "flex flex-wrap gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3"
      }
    >
      <Figure
        label={t("esign.tracking.strip.firstOpen")}
        value={median(data.time_to_first_open)}
        aside={t("esign.tracking.strip.notOpenedYet", {
          count: data.time_to_first_open.running
        })}
      />
      <Figure
        label={t("esign.tracking.strip.cycleTime")}
        value={median(data.cycle_time)}
        aside={t("esign.tracking.strip.stillOut", {
          count: data.cycle_time.running
        })}
      />
      <Figure
        label={t("esign.tracking.strip.ourDelay")}
        value={median(data.our_delay)}
        aside={t("esign.tracking.strip.waitingOnUs", {
          count: data.our_delay.running
        })}
      />
      <Figure
        label={t("esign.tracking.strip.openRate")}
        value={percent(data.open_rate.rate)}
        aside={t("esign.tracking.strip.ofTotal", {
          part: data.open_rate.opened,
          whole: data.open_rate.sent
        })}
      />
      <Figure
        label={t("esign.tracking.strip.declineRate")}
        value={percent(data.decline_rate.rate)}
        aside={t("esign.tracking.strip.ofTotal", {
          part: data.decline_rate.declined,
          whole: data.decline_rate.finished
        })}
      />
    </div>
  )
}
